import { Flex, H1, H2, H3, Icon, P } from 'ui';
import { styled } from 'styled-components';
import { colors, media } from 'theme';
import { RotatedCard } from 'pages/Landing/components/WorkGuide/style';

export const StyledH1 = styled(H1)<{ $isLeader?: boolean }>`
  letter-spacing: 0;
  text-align: ${({ $isLeader }) => ($isLeader ? 'left' : 'center')};
  font-size: ${({ $isLeader }) => ($isLeader ? '3rem' : '4rem')};

  @media (max-width: ${media.laptop}px) {
    font-size: ${({ $isLeader }) => ($isLeader ? '2.25rem' : '3rem')};
  }

  @media (max-width: ${media.mobile}px) {
    font-size: ${({ $isLeader }) => ($isLeader ? '2rem' : '2.25rem')};
  }
`;

export const StyledPBackgroundContainer = styled(P)`
  max-width: 25rem;
`;

type BackgroundContainerFlexProps = {
  $background: string;
};

export const BackgroundContainerFlex = styled(Flex)<BackgroundContainerFlexProps>`
  background: ${({ $background }) => `url(${$background}) no-repeat`};
  background-size: cover;
  min-height: 15rem;
  padding: 6.25rem 0;
  position: relative;
  border-radius: 2rem;
  overflow: hidden;

  @media (max-width: ${media.mobile}px) {
    height: 35.625rem;
    background-size: cover;
    background-position: center center;
  }
`;

export const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const ContentFlex = styled(Flex)`
  max-width: 988px;
  padding: 0.25rem;
  position: relative;

  @media (max-width: ${media.laptop}px) {
    max-width: 700px;
  }
`;

export const CardInfo = styled.div`
  border-radius: 0.75rem;
  border: 2px solid ${colors.whiteTransparent};
  background: ${colors.transparent};
  backdrop-filter: blur(25px);
  padding: 3.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 2.25rem;

  @media (max-width: ${media.mobile}px) {
    padding: 2rem 0.75rem;
    gap: 1.5rem;
  }
`;

export const CustomSpan = styled.span`
  font-size: 3rem;
  line-height: 120%;
  font-style: normal;
  text-transform: uppercase;
`;

export const StyledP = styled(P)`
  height: min-content;
  margin-top: auto;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  width: 100%;
  align-items: stretch;
  justify-content: flex-start;

  @media (max-width: ${media.laptop}px) {
    grid-template-columns: 1fr 1fr;

    & > ${CardInfo}:last-child {
      grid-column: 1 / span 2;
    }

    & > p {
      width: min-content;
    }
  }

  @media (max-width: ${media.mobile}px) {
    grid-template-columns: 1fr;

    & > ${CardInfo}:last-child {
      grid-column: unset;
    }

    & > p {
      width: unset;
    }
  }
`;

type GetToKnowWrapperProps = {
  $background: string;
};

export const GetToKnowWrapper = styled(Flex)<GetToKnowWrapperProps>`
  padding: 6.25rem 0;
  background: ${({ $background }) => `url(${$background}) center center no-repeat`};
  position: relative;
  background-size: cover;
`;

export const GetToKnowFlex = styled(Flex)`
  @media (max-width: ${media.tablet}px) {
    flex-wrap: wrap-reverse;
    justify-content: center;
    align-items: center;
  }
`;

export const GetToKnowCardWrapper = styled(Flex)`
  max-width: 23.875rem;
  width: 100%;
  height: 31.25rem;
  flex-shrink: 0;
  position: relative;
`;

export const GetToKnowCardRotated = styled(RotatedCard)`
  width: 17.75rem;
  height: 20.4375rem;
`;

export const GetToKnowHeader = styled(H2)`
  color: ${({ theme }) => theme.fontsColor.secondary};
  font-size: 1rem;
  font-weight: 400;
`;

export const GetToKnowP = styled(P)`
  font-size: 2.5rem;
  text-transform: uppercase;

  @media (max-width: ${media.mobile}px) {
    font-size: 1.5rem;
  }
`;

export const StyledHeaderGetToKnow = styled(H3)`
  font-size: 2.5rem;
  font-style: normal;
  font-weight: 700;

  @media (max-width: ${media.mobile}px) {
    font-size: 1.5rem;
  }
`;

type GridAboutProps = {
  $background: string;
};

export const GridAbout = styled.div<GridAboutProps>`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, 1fr);
  align-items: stretch;
  justify-content: flex-start;
  padding: 12.5rem 3.75rem 12.5rem 7.5rem;
  gap: 1.5rem;
  background: ${({ $background }) => `url(${$background}) center center no-repeat`};
  background-size: cover;
  position: relative;
  left: -8.75rem;
  box-sizing: content-box;

  @media (max-width: ${media.laptop}px) {
    left: -5rem;
    padding: 12.5rem 3.75rem 12.5rem 5rem;
  }

  @media (max-width: ${media.tablet}px) {
    left: -3.75rem;
    padding: 12.5rem 3.75rem;
  }

  @media (max-width: ${media.mobile}px) {
    grid-template-columns: 1fr;
    left: -1.25rem;
    padding: 3.5rem 1.25rem;
  }
`;

export const GridLeague = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 1.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${media.mobile}px) {
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
`;

export const CardContainer = styled(Flex)`
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
  border: 2px solid ${colors.whiteTransparent};
  background: ${colors.transparent};
  backdrop-filter: blur(25px);
  padding: 4.5rem 2.5rem 2rem 2.5rem;
  grid-row: 1 / span 2;

  @media (max-width: ${media.laptop}px) {
    grid-row: 1;
    grid-column: 1 / span 2;
  }

  @media (max-width: ${media.mobile}px) {
    grid-row: 1;
    grid-column: 1 / span 1;
  }
  &:after {
    content: '';
    position: absolute;
    left: 2rem;
    bottom: 0rem;
    border-radius: 20.70525rem;
    background: #3138e9;
    filter: blur(87.06097412109375px);
    width: 20.70525rem;
    height: 19.15056rem;
    transform: rotate(49.789deg);
    @media (max-width: ${media.laptop}px) {
      right: -11.375rem;
      bottom: -18.375rem;
      left: unset;
    }

    @media (max-width: ${media.tablet}px) {
      right: 9.625rem;
      bottom: -11.375rem;
      left: unset;
    }
  }

  &:before {
    content: '';
    position: absolute;
    left: 2rem;
    bottom: 0rem;
    width: 25.51056rem;
    height: 23.81456rem;
    transform: rotate(49.789deg);
    border-radius: 25.51056rem;
    background: #2b8778;
    filter: blur(60.490413665771484px);

    @media (max-width: ${media.laptop}px) {
      right: -11.375rem;
      bottom: -18.375rem;
      left: unset;
    }

    @media (max-width: ${media.tablet}px) {
      right: 9.625rem;
      bottom: -11.375rem;
      left: unset;
    }
  }
`;

export const CardContainerHeader = styled(H3)`
  font-size: 3rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${media.mobile}px) {
    font-size: 1.5rem;
  }
`;

export const CardLeague = styled(Flex)`
  padding: 4.5rem 2.5rem;
  border-radius: 0.75rem;
  border: 2px solid ${colors.whiteTransparent};
  background: ${colors.transparent};
  backdrop-filter: blur(25px);
`;

export const CardContainerImage = styled.img`
  position: absolute;
  left: -6.5rem;
  bottom: -10.375rem;
  width: 40.1875rem;
  height: 35rem;
  z-index: 1;

  @media (max-width: ${media.laptop}px) {
    right: -12.4375rem;
    bottom: -12.125rem;
    left: unset;
  }

  @media (max-width: ${media.tablet}px) {
    right: 1.5625rem;
    bottom: -17.125rem;
  }

  @media (max-width: ${media.mobile}px) {
    width: 20.0625rem;
    height: 18rem;
    left: -6rem;
    bottom: -3.375rem;
  }
`;

export const CardPrize = styled(Flex)`
  display: flex;
  padding: 2rem 1.5rem;
  border-radius: 12px;
  border: 2px solid ${colors.whiteTransparent};
  background: ${colors.transparent};
  backdrop-filter: blur(25px);
`;

export const CardPrizeImage = styled.img`
  width: 100%;
  height: 16.8125rem;
  object-fit: cover;
`;

export const ProgressCardPrize = styled.div<{ min: number; max: number; current: number }>`
  position: relative;
  width: 100%;
  height: 0.5rem;
  background: ${colors.whiteTransparent};
  border-radius: 0.25rem;
  margin-top: 1.5rem;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: ${({ min, max, current }) =>
      `${((current - min) / (max - min)) * 100 > 100 ? 100 : ((current - min) / (max - min)) * 100}%`};
    height: 100%;
    background: ${colors.yellow};
    border-radius: 0.25rem;
  }
`;

export const PreviewBoard = styled(Flex)`
  position: relative;
  overflow: hidden;
  padding: 20px;
  background: url('https://www.figma.com/file/VHIpa4kbP8PqLwHlSdoQr9/image/045513edd1077a4e72fc87b213056f0fdc8a6ebd')
    no-repeat center center;
  height: 276px;
  background-size: cover;
  border-radius: 12px;
  box-sizing: border-box;
`;

export const RowBoard = styled.div`
  display: grid;
  grid-template-columns: 20px 50% 1fr;
  gap: 8px;
  padding-bottom: 12px;
  width: 100%;
  border-bottom: 1px solid ${colors.whiteTransparent};
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  @media (max-width: ${media.laptop}px) {
    grid-template-columns: 20px 50% 1fr;
  }
`;

export const StyledIcon = styled(Icon)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

export const BlockWrapper = styled(Flex)`
  padding: 6.25rem 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  align-items: stretch;
  justify-content: flex-start;

  @media (max-width: ${media.tablet}px) {
    padding: 3.75rem 0;
  }

  @media (max-width: ${media.mobile}px) {
    grid-template-columns: 1fr;
  }
`;

export const BlockWrapper2 = styled(Flex)`
  padding: 6.25rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: flex-start;
  justify-content: flex-start;

  @media (max-width: ${media.tablet}px) {
    padding: 3.75rem 0;
  }
`;

export const StyledFlex2 = styled(Flex)`
  align-self: flex-start;
`;

export const SuccessArrow = styled(Icon)`
  color: ${colors.success};
  width: 1.5rem;
  height: 1.5rem;
`;

export const PreTitle = styled.pre`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.5rem;
  color: ${colors.white};
  font-weight: 700;
  text-transform: uppercase;
  text-align: left;
`;

export const ScrollableFlex = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(16rem, calc((100% - 40px) / 3)), 1fr));
  width: 100%;
  gap: 20px;
`;
